import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Layer } from 'grommet';
import { StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { StripeGlobalPayoutsService, UpdateBankAccountRequest } from '/src/api/services/StripeGlobalPayoutsService';
import { Box, Card, CardBody, CardHeader, InfoBox, Input, Text } from '/src/components';

export type AddBankAccountModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  clientId: number;
  onBankAccountAdded: () => void;
};

export const AddBankAccountModal: React.FC<AddBankAccountModalProps> = observer(
  ({ isVisible, setIsVisible, clientId, onBankAccountAdded }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [bankData, setBankData] = useState<UpdateBankAccountRequest>({
      routing_number: '',
      account_number: '',
      bank_account_type: 'checking',
    });

    const validateBank = () => {
      const newErrors: Record<string, string> = {};
      if (!bankData.routing_number.trim()) newErrors.routing_number = 'Routing Number is required';
      if (!bankData.account_number.trim()) newErrors.account_number = 'Account Number is required';
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
      if (!validateBank()) return;
      try {
        setIsSubmitting(true);
        await StripeGlobalPayoutsService.updateBankAccount(clientId, bankData);
        toast.success('Bank account updated successfully. Previous bank account has been replaced.');
        setIsVisible(false);
        onBankAccountAdded();
        setErrors({});
        setBankData({
          routing_number: '',
          account_number: '',
          bank_account_type: 'checking',
        });
      } catch (error: unknown) {
        console.error('Error adding bank account:', error);
        const errorMessage =
          (error as unknown as { body?: { error?: { user_message?: string } } })?.body?.error?.user_message ||
          'Failed to update bank account';
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleClose = () => {
      setIsVisible(false);
      setErrors({});
      setBankData({
        routing_number: '',
        account_number: '',
        bank_account_type: 'checking',
      });
    };

    if (!isVisible) return null;

    return (
      <Layer onEsc={handleClose} onClickOutside={handleClose} background="transparent">
        <Card width="100%" style={{ maxWidth: 480, minWidth: 320 }}>
          <CardHeader title="Update Bank Account" />
          <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
            <Box gap="small">
              <InfoBox gap="1rem" direction="row" align="center">
                <StatusWarning size="medium" color="status-warning" />
                <Text>
                  Adding a new bank account will replace your existing bank account. This action cannot be undone.
                </Text>
              </InfoBox>

              <Input
                label="Routing Number"
                value={bankData.routing_number}
                setValue={(value) => setBankData({ ...bankData, routing_number: value })}
                placeholder="Enter routing number"
                required
                error={errors.routing_number}
                style={{ width: '100%' }}
              />

              <Input
                label="Account Number"
                value={bankData.account_number}
                setValue={(value) => setBankData({ ...bankData, account_number: value })}
                placeholder="Enter account number"
                required
                error={errors.account_number}
                style={{ width: '100%' }}
                margin={{ bottom: 'small' }}
              />

              <Box direction="row" gap="small" justify="end">
                <Button label="Cancel" onClick={handleClose} disabled={isSubmitting} style={{ fontWeight: 'normal' }} />
                <Button
                  primary
                  label={isSubmitting ? 'Processing...' : 'Update Bank Account'}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ fontWeight: 'normal' }}
                />
              </Box>
            </Box>
          </CardBody>
        </Card>
      </Layer>
    );
  }
);
